import { SvgData } from "../../model/SvgData";
import { IconoItem } from "./IconoItem";
import { useCallback, useEffect, useRef, useState } from "react";
import { ModuleContainerGeneric } from "../ModuleContainerGeneric";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper";
import { Swiper as SwiperClass } from "swiper/types";
import { BP_MAX_WIDTH } from "../../constants/data";
import { IIndexable } from "../../model/IIndexable";
import { HStack } from "@bpba/ui-components";
import { Titulo } from "./Titulo";
import { GenericComponentProps } from "../../model/GenericComponentProps";
import "swiper/css";
import "swiper/css/navigation";
import { CssSwiper } from "./CssSwiper";

export type IconosSliderProps = {
  config: IIndexable;
} & GenericComponentProps<SvgData>;

export const IconosSlider = (props: IconosSliderProps) => {
  const { config: initialConfig, moduloTitulo, backgroundColor } = props;
  const [config, setConfig] = useState<IIndexable>(initialConfig);
  const ref = useRef<SwiperClass>();

  const setAriaDisabled = (
    elements: NodeListOf<HTMLElement>,
    value: boolean
  ) => {
    elements.forEach((item) =>
      item.setAttribute("aria-disabled", value.toString())
    );
  };

  const updateSlideAccessibility = useCallback((swiper: SwiperClass) => {
    if (!swiper?.slides) return;

    const slidesArray = Array.from(swiper.slides);

    slidesArray.forEach((slide) =>
      slide.classList.remove("swiper-slide-transparent")
    );

    const visibleSlides = slidesArray.filter((slide) => {
      setAriaDisabled(slide.querySelectorAll(":scope > *"), false);
      return slide.classList.contains("swiper-slide-visible");
    });

    /// .swiper-slide-transparent only works on the first and last slide in tablet
    const firstSlide = visibleSlides[0];
    const lastSlide = visibleSlides[visibleSlides.length - 1];
    if (visibleSlides.length > 0) {
      firstSlide.classList.add("swiper-slide-transparent");
      setAriaDisabled(firstSlide.querySelectorAll(":scope > *"), true);
      lastSlide.classList.add("swiper-slide-transparent");
      setAriaDisabled(lastSlide.querySelectorAll(":scope > *"), true);
    }
  }, []);

  const handlePrev = useCallback(() => ref.current?.slidePrev(), []);
  const handleNext = useCallback(() => ref.current?.slideNext(), []);

  const debounceResize = (fn: () => void, delay: number) => {
    let timer: NodeJS.Timeout;
    return () => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(), delay);
    };
  };

  useEffect(() => {
    const onResize = debounceResize(() => {
      if (ref.current) updateSlideAccessibility(ref.current);
      setConfig(initialConfig);
    }, 100);

    onResize();
    window.addEventListener("resize", onResize);

    return () => window.removeEventListener("resize", onResize);
  }, [initialConfig, updateSlideAccessibility]);

  return (
    <ModuleContainerGeneric
      display={["flex"]}
      flexDirection="column"
      className={config?.classContainer}
      paddingX={["8px", "0px", null, null, "calc(107px - 63px)"]}
      maxWidth={[null, null, null, `calc(${BP_MAX_WIDTH} + 126px)`]}
      backgroundColor={backgroundColor ?? "secondary.0"}
      css={CssSwiper}
    >
      {moduloTitulo && <Titulo isStatic={false}>{moduloTitulo}</Titulo>}

      <HStack>
        {config.modules?.includes(Navigation) && (
          <div className="swiper-button-prev" onClick={handlePrev} />
        )}

        <Swiper
          onSwiper={(swiper) => (ref.current = swiper)}
          onSlideChange={updateSlideAccessibility}
          spaceBetween={8}
          modules={config?.modules}
          slidesPerView={config?.slidesPerView}
          centeredSlides={config?.centeredSlides}
          loop={config?.loop}
          keyboard
          watchSlidesProgress
          autoplay={{ delay: 5000 }}
          speed={500}
        >
          {config.slides?.map((item: SvgData, index: number) => (
            <SwiperSlide key={index}>
              <IconoItem data={item} />
            </SwiperSlide>
          ))}
        </Swiper>

        {config.modules?.includes(Navigation) && (
          <div className="swiper-button-next" onClick={handleNext} />
        )}
      </HStack>
    </ModuleContainerGeneric>
  );
};
